/**
 * The Lines dock tab (W2-07; DESIGN.md §4.10): header (count, Add menu, Duplicate, Delete, Tree / Export order,
 * expand / collapse, groups, search), filter chips, the "preview differs from export" summary of the preview-only
 * filters (hide, solo, layer mute, dim; lock never counts and only shows as row badges and a lock count), the
 * "Also show" chips and the tree grid. Everything reads the services, never the DocStore context, so the panel works
 * in any host.
 */

import { useCallback, useMemo, type KeyboardEvent } from 'react';
import type { PanelProps } from '../../app/registry/types';
import type { LineId } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { IconButton } from '../../ui/IconButton';
import { Segmented } from '../../ui/Segmented';
import { cx } from '../../ui/internal/cx';
import { AddLineMenu } from './AddLineMenu';
import { useDocValue, useEditorValue, useExtSetting, useLinesUi, useNow, useServicesStore } from './hooks';
import { deleteFlow, duplicateLines, lineTitle, showAll, targetLines, toggleAlsoShow, unlockAll } from './lineActions';
import { LineFilters } from './LineFilters';
import { m } from './lines.messages';
import {
  buildLineTable,
  expandTo,
  filterLines,
  orderRows,
  previewDifferences,
  treeRows,
  type PreviewDiffSummary,
} from './lineModel';
import { linesUi, VIEW_SETTING, type LinesView } from './linesUi';
import { LineTree } from './LineTree';
import s from './Lines.module.css';

/** Document topics the line table depends on. */
const TABLE_TOPICS = ['lines', 'lineOrder', 'tracks', 'keys', 'notes', 'groups', 'assets'] as const;

/** "2 lines hidden, 1 solo, dimmed" (each part only when active). */
export function diffDetails(t: ReturnType<typeof useT>, d: PreviewDiffSummary): string {
  const parts: string[] = [];
  if (d.hidden > 0) parts.push(t(m.diffHidden, { count: d.hidden }));
  if (d.solo > 0) parts.push(t(m.diffSolo, { count: d.solo }));
  if (d.mutedLayers > 0) parts.push(t(m.diffMuted, { count: d.mutedLayers }));
  if (d.dimmed) parts.push(t(m.diffDimmed));
  return parts.length === 0 ? '' : parts.reduce((a, b) => t(m.listJoin, { a, b }));
}

function sameDiff(a: PreviewDiffSummary | null, b: PreviewDiffSummary | null): boolean {
  if (a === null || b === null) return a === b;
  return a.hidden === b.hidden && a.solo === b.solo && a.mutedLayers === b.mutedLayers && a.dimmed === b.dimmed;
}

export function LinesPanel(props: PanelProps) {
  const t = useT();
  const store = useServicesStore(props.services);
  if (store === null) {
    return (
      <div className={s.panel} data-region="lines">
        <div className={s.empty}>
          <EmptyState icon="ListTree" title={{ key: m.noProjectTitle }} body={{ key: m.noProject }} />
        </div>
        <span className={s.visuallyHidden}>{t(m.panelTitle)}</span>
      </div>
    );
  }
  return <LinesPanelBody services={props.services} store={store} />;
}

function LinesPanelBody(props: PanelProps & { readonly store: DocStore }) {
  const { services, store } = props;
  const t = useT();
  const table = useDocValue(store, buildLineTable, TABLE_TOPICS);
  const doc = store.doc;
  const ui = useLinesUi(services, (st) => st);
  const view: LinesView = useExtSetting(services, VIEW_SETTING) === 'order' ? 'order' : 'tree';
  const level = useEditorValue(services, (st) => st.level);
  const now = useNow(services, true);
  const filters = ui.filters;
  const needNow = filters.visibleNow || filters.animatingNow;
  const matches = useMemo(() => filterLines(table, filters, needNow ? now : null), [table, filters, needNow, now]);
  const rows = useMemo(
    () => (view === 'tree' ? treeRows(table, ui.collapsed, matches) : orderRows(table, matches)),
    [view, table, ui.collapsed, matches],
  );
  const shown = matches === null ? table.order.length : matches.size;
  const diff = useEditorValue(services, (st) => previewDifferences(st.filters, (id) => table.byId.has(id)), sameDiff);
  const lockedCount = useEditorValue(services, (st) => {
    let n = 0;
    for (const id of st.filters.lockedLines) if (table.byId.has(id)) n++;
    return n;
  });
  const alsoShow = useEditorValue(services, (st) => st.alsoShow);
  const hasParents = useMemo(() => [...table.byId.values()].some((i) => i.children.length > 0), [table]);

  const onToggleCollapse = useCallback(
    (id: LineId): void => {
      const lu = linesUi(services);
      const next = new Set(lu.get().collapsed);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      lu.set({ collapsed: next });
    },
    [services],
  );
  const onExpandTo = useCallback(
    (id: LineId): void => {
      const lu = linesUi(services);
      const next = expandTo(store.doc, lu.get().collapsed, id);
      if (next !== null) lu.set({ collapsed: next });
    },
    [services, store],
  );

  const setText = (text: string): void => {
    const lu = linesUi(services);
    lu.set({ filters: { ...lu.get().filters, text } });
  };
  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape' && e.currentTarget.value !== '') {
      e.preventDefault();
      e.stopPropagation();
      setText('');
    }
  };

  const total = table.order.length;
  let body;
  if (total === 0) {
    body = (
      <div className={s.empty}>
        <EmptyState
          icon="ListTree"
          title={{ key: m.emptyTitle }}
          body={{ key: m.emptyBody }}
          actions={[{ label: { key: m.cmdAddLine }, command: 'lines.add' }]}
        />
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <div className={s.empty}>
        <EmptyState
          icon="FilterX"
          title={{ key: m.noMatchTitle }}
          body={{ key: m.noMatchBody }}
          actions={[{ label: { key: m.clearFilters }, command: 'lines.clearFilters' }]}
        />
      </div>
    );
  } else {
    body = (
      <LineTree
        services={services}
        store={store}
        rows={rows}
        view={view}
        now={now}
        renaming={ui.renaming}
        reveal={ui.reveal}
        label={t(m.treeLabel, { count: rows.length })}
        onToggleCollapse={onToggleCollapse}
        onExpandTo={onExpandTo}
      />
    );
  }

  return (
    <div className={s.panel} data-region="lines" data-help-id="lines.panel">
      <header className={s.header}>
        <span className={s.title}>
          {shown === total ? t(m.panelHeading, { total }) : t(m.panelHeadingFiltered, { total, shown })}
        </span>
        <div className={s.headerGroup}>
          <AddLineMenu full={level === 'full'} />
          <IconButton
            size="sm"
            icon="Copy"
            label={{ key: m.cmdDuplicate }}
            command="lines.duplicate"
            onClick={() => {
              duplicateLines(services, targetLines(services), { withNotes: true, withChildren: false, asChild: false });
            }}
          />
          <IconButton
            size="sm"
            icon="Trash2"
            label={{ key: m.cmdDelete }}
            command="lines.delete"
            onClick={() => {
              void deleteFlow(services, targetLines(services));
            }}
          />
        </div>
        <Segmented<LinesView>
          size="sm"
          ariaLabel={t(m.viewLabel)}
          value={view}
          options={[
            { value: 'tree', label: <span className={s.segText}>{t(m.viewTree)}</span>, icon: 'ListTree' },
            { value: 'order', label: <span className={s.segText}>{t(m.viewOrder)}</span>, icon: 'ListOrdered' },
          ]}
          onChange={(v) => {
            services.settings.setExt(VIEW_SETTING, v);
          }}
        />
        {view === 'tree' && hasParents && (
          <div className={s.headerGroup}>
            <IconButton
              size="sm"
              icon="ChevronsUpDown"
              label={{ key: m.cmdExpandAll }}
              onClick={() => {
                linesUi(services).set({ collapsed: new Set() });
              }}
            />
            <IconButton
              size="sm"
              icon="ChevronsDownUp"
              label={{ key: m.cmdCollapseAll }}
              onClick={() => {
                const parents = new Set<LineId>();
                for (const info of table.byId.values()) if (info.children.length > 0) parents.add(info.id);
                linesUi(services).set({ collapsed: parents });
              }}
            />
          </div>
        )}
        <IconButton
          size="sm"
          icon="Tags"
          label={{ key: m.cmdGroups }}
          onClick={() => {
            linesUi(services).set({ modal: { kind: 'groups' } });
          }}
        />
        <span className={s.spacer} />
        <input
          type="search"
          className={s.search}
          value={filters.text}
          placeholder={t(m.searchPlaceholder)}
          aria-label={t(m.searchLabel)}
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => {
            setText(e.target.value);
          }}
          onKeyDown={onSearchKey}
        />
      </header>
      <LineFilters services={services} doc={doc} table={table} filters={filters} />
      {diff !== null && (
        <div className={cx(s.status, s.statusDiffers)} role="status" data-preview-differs="">
          <span className={s.statusIcon}>
            <Icon name="EyeOff" size={14} />
          </span>
          <span>{t(m.previewDiffers, { details: diffDetails(t, diff) })}</span>
          <button
            type="button"
            className={s.linkButton}
            onClick={() => {
              showAll(services);
            }}
          >
            {t(m.showAll)}
          </button>
        </div>
      )}
      {lockedCount > 0 && (
        <div className={cx(s.status, s.statusMuted)} data-locked-count="">
          <Icon name="Lock" size={12} />
          <span>{t(m.lockedCount, { count: lockedCount })}</span>
          <button
            type="button"
            className={s.linkButton}
            onClick={() => {
              unlockAll(services);
            }}
          >
            {t(m.cmdUnlockAll)}
          </button>
        </div>
      )}
      {alsoShow.length > 0 && (
        <div className={s.status} role="group" aria-label={t(m.alsoShowLabel)}>
          <span className={s.chipLabel}>{t(m.alsoShowLabel)}</span>
          {alsoShow
            .filter((id) => doc.lines.has(id))
            .map((id) => (
              <span key={id} className={s.chip} data-on="true">
                {lineTitle(store, id)}
                <button
                  type="button"
                  className={s.chipRemove}
                  aria-label={t(m.alsoShowRemove, { line: lineTitle(store, id) })}
                  onClick={() => {
                    toggleAlsoShow(services, [id]);
                  }}
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))}
          <button
            type="button"
            className={s.chip}
            onClick={() => {
              linesUi(services).set({ modal: { kind: 'picker', mode: { kind: 'alsoShow' } } });
            }}
          >
            <Icon name="Plus" size={12} />
            {t(m.alsoShowAdd)}
          </button>
        </div>
      )}
      {body}
    </div>
  );
}
